import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ROUTES from "../router/routesModel";
import { isNativePlatform } from "../lib/nativeAuth";
import { Z, CHROME_INSET } from "../styles/zLayers";
import { dismissTopDismissible, hasDismissible } from "./dismissibleStack";
import { isKeyboardShown } from "./keyboardInset";

/**
 * Android hardware Back. See the Google Play launch checklist.
 *
 * THE PROBLEM
 * -----------
 * Nothing in this app listened for `backButton`, so inside the Capacitor wrap
 * every press went to Capacitor's default handler and closed the app — from a
 * trade page, from an open sheet, from the middle of a match session. That is
 * the single loudest "this is a website in a box" tell an Android tester meets,
 * and it costs them their session every time they reach for the gesture they
 * use in every other app on the phone.
 *
 * THE CHAIN
 * ---------
 *   1. An open overlay?          close the top-most one (see dismissibleStack).
 *   2. A focused text field?     blur it, which drops the soft keyboard.
 *   3. Somewhere to go back to?  router back.
 *   4. On a home screen?         arm, then exit on a second press.
 *
 * WHY "PRESS BACK AGAIN TO EXIT" AND NOT A PLAIN EXIT
 * ---------------------------------------------------
 * The home screen here is the map, and the map is where a match session lives —
 * a queue of traders the user is part-way through. Exiting on the first press
 * throws that away silently, and Back is a gesture people fire off half by
 * reflex at the bottom edge of the screen. Every other step in this chain is
 * reversible; only this one is not, so it is the one that gets a confirmation.
 * The cost is one extra press when the user genuinely means to leave, against
 * losing a session to a mis-swipe — and the hint makes the second press
 * obvious rather than a hidden trick.
 *
 * NOTE ON STEP 2. Android normally dismisses the soft keyboard itself: with the
 * IME showing, the framework consumes Back before the WebView ever sees it, so
 * this branch is not what closes the keyboard in the usual case. It matters
 * afterwards — the WebView keeps DOM focus on the field once the IME is gone,
 * and an Android user expects Back to take them out of the field before it
 * takes them off the screen. Blurring is deliberately plugin-free: it is real
 * focus handling rather than a second source of truth about the keyboard, and
 * it leaves `@capacitor/keyboard` free to own keyboard state on its own.
 *
 * NATIVE SIDE. No Java change is needed. `@capacitor/app`'s AppPlugin registers
 * through `getActivity().getOnBackPressedDispatcher().addCallback(...)`, so it
 * never depended on the `onBackPressed()` override that stops being called at
 * targetSdk 36 (verified against the plugin source, not the docs). Registering
 * a JS listener does mean Capacitor stops handling Back itself, which is why
 * step 4 has to call `App.exitApp()` explicitly.
 *
 * A no-op on web: the listener is never registered, and `@capacitor/app` is
 * imported lazily so the web bundle and the Jest environment never load it.
 */

/** How long a first press stays "armed" before Back means exit. */
export const EXIT_CONFIRM_WINDOW_MS = 2000;

/**
 * Screens where Back means "leave the app" rather than "go back one".
 *
 * `/map` is the signed-in home and `/login` the signed-out one; `/` is the
 * redirect stub that leads to whichever applies. Everything else in the app is
 * reached from one of these.
 */
export const HOME_ROUTES = [ROUTES.ROOT, ROUTES.MAP, ROUTES.LOGIN];

export const BACK_ACTIONS = {
  DISMISSED_OVERLAY: "dismissed-overlay",
  BLURRED_INPUT: "blurred-input",
  HISTORY_BACK: "history-back",
  WENT_HOME: "went-home",
  EXIT_ARMED: "exit-armed",
  EXIT: "exit",
};

export const isHomeRoute = (pathname) => {
  const path = String(pathname || "").replace(/\/+$/, "") || "/";
  return HOME_ROUTES.includes(path);
};

/**
 * How many entries React Router has pushed behind us.
 *
 * React Router v6 stamps `{ idx }` into history state on every navigation, so
 * `idx > 0` means there is an in-app entry to unwind. `history.length` is the
 * usual guess and it is wrong: it counts the whole tab's history including
 * whatever preceded the app, and it never shrinks after a `back()`.
 */
export const historyIndex = () => {
  const idx = window?.history?.state?.idx;
  return Number.isInteger(idx) && idx > 0 ? idx : 0;
};

/** Input types that never raise the soft keyboard. */
const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

/** The focused element if it is a text field the keyboard would be up for. */
export const focusedTextField = (doc = typeof document === "undefined" ? null : document) => {
  const el = doc?.activeElement;
  if (!el || el === doc.body) return null;
  if (el.isContentEditable) return el;
  if (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA") return null;
  if (el.disabled || el.readOnly) return null;
  if (el.tagName === "INPUT" && NON_TEXT_INPUT_TYPES.has(String(el.type).toLowerCase())) {
    return null;
  }
  return el;
};

/**
 * The whole priority chain, as a pure function of the four things it depends
 * on. Split out from the side effects so the ordering can be asserted directly
 * rather than inferred from what happened to move on screen.
 */
export const decideBackAction = ({
  overlayOpen,
  textFieldFocused,
  homeRoute,
  canGoBack,
  exitArmed,
}) => {
  if (overlayOpen) return BACK_ACTIONS.DISMISSED_OVERLAY;
  if (textFieldFocused) return BACK_ACTIONS.BLURRED_INPUT;
  if (homeRoute) return exitArmed ? BACK_ACTIONS.EXIT : BACK_ACTIONS.EXIT_ARMED;
  if (canGoBack) return BACK_ACTIONS.HISTORY_BACK;
  // Off the home screens with nothing behind us: a cold deep link (an auth
  // callback, a notification tap). Exiting from here would be a surprise, so
  // go home instead and let the next press decide about leaving.
  return BACK_ACTIONS.WENT_HOME;
};

/**
 * Read the world, decide, act. Returns the action taken so the caller can
 * drive the exit hint — and so a test can watch the chain, not the symptoms.
 */
/**
 * The top-most open MUI Popover — every `<Menu>` and every `<Select>`'s option
 * list renders as one. They never register with the dismissible stack, so Back
 * walked past them: on a home tab it armed "press again to exit" with the menu
 * still open, and inside a page it navigated away with it open (tablet QA,
 * versionCode 11: the listings Type select, the chat and profile ⋮ menus).
 * A `keepMounted` popover that is closed carries MuiModal-hidden; skip it.
 */
export const openMuiPopover = (doc = typeof document === "undefined" ? null : document) => {
  const open = doc?.querySelectorAll?.(".MuiPopover-root:not(.MuiModal-hidden)");
  return open && open.length ? open[open.length - 1] : null;
};

export const handleBackPress = ({ navigate, pathname, exitApp, exitArmed = false }) => {
  // A popover sits above everything the stack holds (a Select inside a
  // registered dialog closes before the dialog), so it goes first. Escape is
  // the close MUI already wires for it — onClose(event, "escapeKeyDown") — and
  // MUI stops that keydown's propagation, so escapeToDismiss does not also see
  // it and close the dialog underneath.
  const popover = openMuiPopover();
  if (popover) {
    popover.dispatchEvent(
      new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true })
    );
    return BACK_ACTIONS.DISMISSED_OVERLAY;
  }

  // Only a field with the keyboard actually up: Android drops the keyboard on
  // the first Back by itself, so a focused field with no keyboard showing is
  // not something this press can close (T-69, tablet QA versionCode 11).
  const field = isKeyboardShown() ? focusedTextField() : null;
  const action = decideBackAction({
    overlayOpen: hasDismissible(),
    textFieldFocused: Boolean(field),
    homeRoute: isHomeRoute(pathname),
    canGoBack: historyIndex() > 0,
    exitArmed,
  });

  switch (action) {
    case BACK_ACTIONS.DISMISSED_OVERLAY:
      dismissTopDismissible();
      break;
    case BACK_ACTIONS.BLURRED_INPUT:
      field.blur();
      break;
    case BACK_ACTIONS.HISTORY_BACK:
      navigate(-1);
      break;
    case BACK_ACTIONS.WENT_HOME:
      // ROUTES.ROOT already forks to /map or /login on auth state, so this
      // needs to know nothing about the user.
      navigate(ROUTES.ROOT, { replace: true });
      break;
    case BACK_ACTIONS.EXIT:
      exitApp?.();
      break;
    default:
      // EXIT_ARMED: nothing to do here; the caller shows the hint.
      break;
  }

  return action;
};

/**
 * Mount the native Back listener. Returns whether the "press again" hint is
 * currently showing.
 *
 * Must be called from inside the router context — it navigates.
 */
export const useAndroidBackButton = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [exitHintVisible, setExitHintVisible] = useState(false);

  // The listener is registered once and then survives every navigation, so it
  // reads the current path through a ref. Putting `pathname` in the effect's
  // dependencies instead would detach and re-attach the native listener on
  // every route change, and a Back arriving inside that gap does nothing.
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  const armedUntilRef = useRef(0);
  const hintTimerRef = useRef(null);

  useEffect(() => {
    if (!isNativePlatform()) return undefined;

    let removeListener = () => {};
    let cancelled = false;

    (async () => {
      const { App } = await import("@capacitor/app");
      if (cancelled) return;

      const handle = await App.addListener("backButton", () => {
        const action = handleBackPress({
          navigate,
          pathname: pathnameRef.current,
          exitApp: () => App.exitApp(),
          exitArmed: Date.now() < armedUntilRef.current,
        });

        if (action === BACK_ACTIONS.EXIT_ARMED) {
          armedUntilRef.current = Date.now() + EXIT_CONFIRM_WINDOW_MS;
          setExitHintVisible(true);
          window.clearTimeout(hintTimerRef.current);
          hintTimerRef.current = window.setTimeout(
            () => setExitHintVisible(false),
            EXIT_CONFIRM_WINDOW_MS
          );
        } else if (armedUntilRef.current) {
          // Any other action means the user went somewhere; a stale arm must
          // not let a much later press exit without its own warning.
          armedUntilRef.current = 0;
          window.clearTimeout(hintTimerRef.current);
          setExitHintVisible(false);
        }
      });

      removeListener = () => handle.remove();
      if (cancelled) removeListener();
    })();

    return () => {
      cancelled = true;
      removeListener();
      window.clearTimeout(hintTimerRef.current);
    };
  }, [navigate]);

  return exitHintVisible;
};

const hintStyle = {
  position: "fixed",
  left: "50%",
  transform: "translateX(-50%)",
  bottom: CHROME_INSET.bottom,
  zIndex: Z.snackbar,
  padding: "10px 18px",
  borderRadius: "999px",
  background: "rgba(24,16,38,0.92)",
  color: "#fff",
  fontSize: "0.85rem",
  fontWeight: 600,
  letterSpacing: "0.01em",
  boxShadow: "0 8px 24px rgba(0,0,0,0.28)",
  pointerEvents: "none",
};

/**
 * Mounts the Back listener and renders the exit hint. Rendered from `Router`
 * so it sits inside the router context it navigates with — the same place
 * `useAuthDeepLink` lives, and for the same reason.
 *
 * Renders nothing at all until a first Back on a home screen arms the exit.
 */
const AndroidBackHandler = () => {
  const exitHintVisible = useAndroidBackButton();
  if (!exitHintVisible) return null;
  return (
    <div role="status" aria-live="polite" data-testid="back-exit-hint" style={hintStyle}>
      Press back again to exit
    </div>
  );
};

export default AndroidBackHandler;
